import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Drawer, Spin, Table, Tag} from 'antd'
import {GROUP_VERIFY_LABELS} from '../../constants'
import {DASH, formatDate, formatDateTime, formatExactRate, formatInt} from '../../helpers/format'
import LoadError from '../shared/LoadError'
import Notice from '../shared/Notice'
import {isEffectiveAt} from '../../helpers/priority'
import {getStageGroupsHistory} from '../../../../services/api/apiProductionPlanning'

/**
 * Lịch sử cấu hình của một công đoạn (SCR-M16-002).
 *
 * Hai bảng TÁCH RỜI, cố ý không gộp thành một dòng thời gian:
 *   · "Các bản của nhóm" trả lời *chỉ số lúc đó là bao nhiêu, áp cho phạm vi nào*;
 *   · "Nhật ký lưu" trả lời *ai bấm lưu lúc nào, đổi bao nhiêu nhóm, vì sao*.
 * Một lượt lưu chạm nhiều nhóm, nên gộp lại là mất khả năng trả lời câu thứ nhất.
 *
 * ⚠️ Bảng nhóm gồm cả bản ĐÃ HẾT HIỆU LỰC — đó chính là phần lịch sử. Đừng lọc `expired` đi cho
 *    "gọn": lọc xong thì màn này chỉ còn đúng thứ màn cấu hình đã hiện, tức không còn lý do tồn tại.
 */
function HistoryDrawer({stageCode, stageName, visible, onClose}) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchHistory = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            setData(await getStageGroupsHistory(stageCode))
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
    }, [stageCode])

    // Chỉ tải khi MỞ: người dùng hiếm khi xem lịch sử, tải sẵn theo màn cấu hình là thêm một request
    // cho mọi lần đổi công đoạn mà gần như không ai dùng tới.
    useEffect(() => {
        if (visible) fetchHistory()
    }, [visible, fetchHistory])

    const groupColumns = [
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            width: 130,
            /*
             * Chấm bằng KHOẢNG HIỆU LỰC, không bằng `status`.
             *
             * ⚠️ `status` chỉ nói "đã bị lượt lưu sau thay hay chưa" — nó KHÔNG nói nhóm có đang chạy
             *    không. Nhóm khai `01/01 → 01/04` vẫn `status: 'active'`, nên bản trước gắn thẻ xanh
             *    "Đang hiệu lực" ngay cạnh cột ghi rõ khoảng đã đóng từ 5 tháng trước — ở đúng màn
             *    hình IE mở ra để hỏi "lúc đó cấu hình thế nào". Và màn Cấu hình thì gắn "Đã hết hiệu
             *    lực" cho cùng nhóm đó: hai màn nói ngược nhau.
             */
            render: (status, row) => {
                if (status !== 'active') return <Tag>Đã bị thay</Tag>

                if (isEffectiveAt(row)) return <Tag color="green">Đang hiệu lực</Tag>

                const notYet = !!row.effectiveFrom && new Date(row.effectiveFrom).getTime() > Date.now()

                return <Tag color="orange">{notYet ? 'Chưa tới hiệu lực' : 'Đã hết hiệu lực'}</Tag>
            },
        },
        {title: 'Bản', dataIndex: 'version', width: 60, align: 'right', render: (v) => `v${v}`},
        {
            title: 'Phạm vi',
            key: 'scope',
            render: (text, row) => (
                <span>
                    {row.skuScope === 'all'
                        ? <Tag>Mọi SKU</Tag>
                        : <Tag color="blue">{formatInt((row.skuList || []).length)} SKU</Tag>}
                    {(row.materialCodes || []).map((code) => (
                        <code key={code} className="VcMaterialCode">{code}</code>
                    ))}
                </span>
            ),
        },
        {
            title: 'Chỉ số',
            dataIndex: 'rate',
            width: 90,
            align: 'right',
            /*
             * `formatInt`-kiểu làm tròn LÊN là SAI ở đây. `formatRate` gọi `ceilTo`, nên bản lịch sử
             * ghi 0,4 hiện thành "1" — mà service CỐ Ý không backfill bản đã đóng khoảng (sửa số của
             * lịch sử là viết lại quá khứ). Màn này tồn tại để trả lời "lúc đó chỉ số là bao nhiêu";
             * làm tròn câu trả lời là hỏng đúng lý do tồn tại của nó.
             */
            render: (rate) => <code className="VcRate">{formatExactRate(rate)}</code>,
        },
        {
            title: 'Hiệu lực',
            key: 'effective',
            width: 190,
            // Cận trên LOẠI TRỪ (`effectiveFrom <= t < effectiveTo`), nên bản còn hiệu lực để trống
            // vế sau chứ không phải "đến hôm nay".
            render: (text, row) => (
                <span>
                    {formatDate(row.effectiveFrom)} → {row.effectiveTo ? formatDate(row.effectiveTo) : <span className="VcMuted">nay</span>}
                </span>
            ),
        },
        {
            title: 'Xác nhận',
            dataIndex: 'verifyStatus',
            width: 120,
            render: (verifyStatus) => (verifyStatus === 'verified'
                ? <Tag color="green">{GROUP_VERIFY_LABELS.verified}</Tag>
                : <Tag>{GROUP_VERIFY_LABELS.unverified}</Tag>),
        },
        {
            title: 'Lý do thay đổi',
            dataIndex: 'changeReason',
            render: (reason) => (reason ? <span>{reason}</span> : DASH),
        },
    ]

    const entryColumns = [
        {title: 'Lúc', dataIndex: 'at', width: 170, render: (at) => formatDateTime(at)},
        {title: 'Người', dataIndex: 'actor', width: 120},
        {
            title: 'Thay đổi',
            key: 'counts',
            render: (text, row) => {
                const p = row.payload || {}

                return (
                    <span>
                        <Tag color="blue">{formatInt(p.createdCount || 0)} mới</Tag>
                        <Tag>{formatInt(p.updatedCount || 0)} sửa</Tag>
                        {!!p.expiredCount && <Tag color="orange">{formatInt(p.expiredCount)} hết hiệu lực</Tag>}
                    </span>
                )
            },
        },
        {
            title: 'Chỉ số đã đổi',
            key: 'rates',
            width: 200,
            /**
             * ⚠️ Đây là nơi DUY NHẤT còn giữ chỉ số CŨ của một nhóm bị sửa tại chỗ.
             *
             * Chỉ nhóm bị GỠ khỏi lượt lưu mới được chuyển sang hết hiệu lực và giữ nguyên số cũ.
             * Nhóm giữ nguyên `id` — tức thao tác phổ biến nhất, sửa chỉ số — bị ghi đè, `version`
             * tăng nhưng bản cũ không còn trong bảng phía trên. Không render cột này thì câu hỏi kinh
             * điển của IE ("tuần trước chỉ số là bao nhiêu?") không trả lời được, dù dữ liệu đang nằm
             * ngay đây.
             */
            render: (text, row) => {
                const changes = (row.payload || {}).rateChanges || []

                if (!changes.length) return DASH

                return (
                    <span>
                        {changes.map((change) => (
                            <code key={change.groupId} className="VcRate VcRateChange">
                                {formatExactRate(change.from)} → {formatExactRate(change.to)}
                            </code>
                        ))}
                    </span>
                )
            },
        },
        {
            title: 'Lý do',
            key: 'reason',
            render: (text, row) => ((row.payload || {}).changeReason || DASH),
        },
    ]

    const renderBody = () => {
        if (error) return <LoadError error={error} onRetry={fetchHistory}/>
        if (loading || !data) return <div className="VcPageLoading"><Spin tip="Đang tải lịch sử…"/></div>

        return (
            <>
                <Notice level="info">
                    Hai bảng dưới đây trả lời hai câu khác nhau, đọc kèm nhau mới đủ. Nhóm bị{' '}
                    <strong>gỡ khỏi</strong> một lượt lưu được chuyển sang hết hiệu lực và giữ nguyên chỉ
                    số lúc đó — xem ở bảng trên. Nhóm được <strong>sửa tại chỗ</strong> thì chỉ số cũ
                    không còn ở bảng trên (bản ghi bị ghi đè, chỉ tăng số bản); chênh lệch của nó nằm ở
                    cột “Chỉ số đã đổi” của nhật ký bên dưới.
                </Notice>

                <div className="VcColTitle mt-3">
                    Các bản của nhóm ({formatInt(data.groups.length)}
                    {data.groupsTruncated && ' mới nhất'})
                </div>
                {data.groupsTruncated && (
                    <Notice level="warning">
                        Chỉ hiện {formatInt(data.limit)} bản MỚI NHẤT — công đoạn này còn nhiều hơn thế.
                        Bản cũ hơn chưa xem được ở đây.
                    </Notice>
                )}
                <Table
                    className="VcTable"
                    rowKey={(row, index) => `${row.id}-${row.version}-${index}`}
                    size="small"
                    pagination={false}
                    columns={groupColumns}
                    dataSource={data.groups}
                    rowClassName={(row) => (row.status === 'active' ? '' : 'VcRow--unconfigured')}
                />

                <div className="VcColTitle mt-4">
                    Nhật ký lưu ({formatInt(data.entries.length)}
                    {data.entriesTruncated && ' mới nhất'})
                </div>
                {data.entries.length ? (
                    <Table
                        className="VcTable"
                        rowKey={(row, index) => `${row.at}-${index}`}
                        size="small"
                        pagination={false}
                        columns={entryColumns}
                        dataSource={data.entries}
                    />
                ) : (
                    // Nói rõ VÌ SAO trống, không để bảng rỗng: nhật ký chỉ ghi từ lần deploy này trở
                    // đi, nên công đoạn cấu hình từ trước sẽ trống ở đây mà vẫn có bản nhóm ở trên.
                    <Notice level="info">
                        Chưa có lượt lưu nào được ghi nhật ký. Nhật ký chỉ ghi từ khi tính năng này được
                        bật — các lần sửa trước đó vẫn xem được ở bảng bản nhóm phía trên.
                    </Notice>
                )}
            </>
        )
    }

    return (
        <Drawer
            title={`Lịch sử cấu hình: ${stageName || stageCode}`}
            width={980}
            visible={visible}
            onClose={onClose}
            destroyOnClose
        >
            {renderBody()}
        </Drawer>
    )
}

HistoryDrawer.propTypes = {
    stageCode: PropTypes.string.isRequired,
    stageName: PropTypes.string,
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
}

HistoryDrawer.defaultProps = {
    stageName: '',
}

export default HistoryDrawer
